import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the Fixture slice
const initialState = {
  status: "idle",
  error: null,
  courseidFixtureList: [],
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Async thunk for fetching the list a zone fixtures.
 * @returns {Array} - List of zone fixtures.
 */
export const zoneFixtureList = createAsyncThunk(
  "listZoneFixture",
  async ({ zoneId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/courses/fixtures/${zoneId}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.listZoneFixture.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for adding a new Fixture.
 * @param {Object} data - The Fixture data to be added.
 * @returns {Object} - The added Fixture data.
 */
export const addFixture = createAsyncThunk(
  "createFixture",
  async ({ submissionData, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: submissionData,
          url: "admin/courses/fixtures",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.createFixture.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for adding a new Fixture.
 * @param {Object} data - The Fixture data to be added.
 * @returns {Object} - The added Fixture data.
 */
export const editFixture = createAsyncThunk(
  "updateFixture",
  async ({ submissionData, zoneId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: submissionData,
          url: `admin/courses/fixtures`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.updateFixture.error, {
        cause: err.cause,
      });
    }
  }
);

// Create a slice for Fixture-related actions and state management
const fixtureSlice = createSlice({
  name: "fixtures",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, FixtureState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, FixtureState);
    },
    removeFixtureDataByID: (state, action) => {
      state.courseidFixtureList = state.courseidFixtureList.filter(
        (fixture) => fixture.id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            addFixture.pending,
            editFixture.pending,
            zoneFixtureList.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          action.meta.arg.setLoader(true);
        }
      )
      .addMatcher(
        (action) =>
          [
            addFixture.fulfilled,
            editFixture.fulfilled,
            zoneFixtureList.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";
          action.meta.arg.setLoader(false);
          if (["listZoneFixture"].includes(action.type.split("/")[0])) {
            state.courseidFixtureList = action.payload.courseFixtures;
          }

          // Show toast only in case of add, edit, delete fixtures
          if (
            ["createFixture/fulfilled", "updateFixture/fulfilled"].includes(
              action.type
            )
          ) {
            fixtureSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  messages[action.type.split("/")[0]].success,
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            addFixture.rejected,
            editFixture.rejected,
            zoneFixtureList.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          action.meta.arg.setLoader(false);
          fixtureSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

// Action creators are generated for each case reducer function
export const { removeFixtureDataByID } = fixtureSlice.actions;

export default fixtureSlice.reducer;
